import { useEffect, useRef } from 'react';
import { LoaderCircleIcon, SquareIcon, XIcon } from 'lucide-react';
import type { ChatDictationStatus } from '../hooks/useChatDictation';
import { ChatDictationWaveform } from './ChatDictationWaveform';
import './chat-dictation.css';

const ROUND_BUTTON = 'inline-flex size-9 shrink-0 items-center justify-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none';
const TRANSCRIBING_LABEL = 'Transcription en cours…';

/** Replaces the composer row while dictating: cancel · live level meter · stop-and-transcribe. */
export function ChatDictationBar({
  analyser,
  onCancel,
  onStop,
  status,
}: {
  analyser: AnalyserNode | null;
  onCancel: () => void;
  onStop: () => void;
  status: Exclude<ChatDictationStatus, 'idle'>;
}) {
  const stopButtonRef = useRef<HTMLButtonElement | null>(null);
  const isTranscribing = status === 'transcribing';

  useEffect(() => {
    // Keyboard users land on the primary action: Enter stops and transcribes.
    if (status === 'recording') {
      stopButtonRef.current?.focus();
    }
  }, [status]);

  return (
    <div
      className="t-dictation-enter grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-1"
      data-testid="chat-dictation-bar"
    >
      <button
        aria-label={isTranscribing ? 'Annuler la transcription' : 'Annuler la dictée'}
        className={`${ROUND_BUTTON} bg-black/[0.05] text-foreground/70 hover:bg-black/[0.09] hover:text-foreground dark:bg-white/[0.08] dark:hover:bg-white/[0.14]`}
        data-testid="chat-dictation-cancel-button"
        onClick={onCancel}
        type="button"
      >
        <XIcon className="size-[1.1rem] stroke-[2.35]" />
      </button>

      <div className="flex min-h-[2.25rem] min-w-0 items-center" aria-live="polite">
        {isTranscribing ? (
          // Padding lives on the wrapper: the shimmer ::before layer is positioned on the
          // label box and must start exactly where the glyphs start, or it ghosts the text.
          <span className="px-1">
            <span
              className="t-shimmer text-[1.05rem] leading-7"
              data-testid="chat-dictation-transcribing"
              data-text={TRANSCRIBING_LABEL}
              role="status"
            >
              {TRANSCRIBING_LABEL}
            </span>
          </span>
        ) : (
          <>
            <span className="sr-only" role="status">
              {status === 'starting' ? 'Activation du micro…' : 'Enregistrement en cours'}
            </span>
            <ChatDictationWaveform analyser={analyser} />
          </>
        )}
      </div>

      <button
        ref={stopButtonRef}
        aria-label={isTranscribing ? 'Transcription en cours' : 'Arrêter et transcrire'}
        className={`${ROUND_BUTTON} bg-foreground text-background hover:bg-foreground/90 disabled:bg-black/[0.05] disabled:text-foreground/40 dark:disabled:bg-white/10`}
        data-testid="chat-dictation-stop-button"
        disabled={status !== 'recording'}
        onClick={onStop}
        type="button"
      >
        {isTranscribing ? (
          <LoaderCircleIcon className="size-[1.05rem] animate-spin motion-reduce:animate-none" />
        ) : (
          <SquareIcon className="size-[0.85rem] fill-current stroke-[2.4]" />
        )}
      </button>
    </div>
  );
}
